/**
 * V113-7-4 — Shared feeds on the Monitoring page: pack validity (status, history per pack newest first, reasons,
 * next at risk, "Sign a new pack"), and per recipient the public link (Copy / Rotate / Open as the recipient sees it)
 * with Issue (Insurer / Customer), Replace token and Revoke. The one-time token shows once in a dialog whose Done
 * stays disabled until the token was copied or "I saved the token" is ticked. Feeds: owner / admin (the server is
 * the authority; other roles see who to ask).
 */
import { Fragment, useEffect, useId, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { monitoringKeys } from '../../hooks/useMonitoring';
import { Link } from 'react-router-dom';
import { Alert, Button, Checkbox, Field, Input, Radio, Spinner, StatusGlyph } from '@aigovops/design-system';
import type { PackHistoryEntry, PackValidity } from '@aigovops/core';
import { ActionDialog, useFocusAfter } from '../today/shared';
import { useIssueFeed, useMonitoringFeeds, useRevokeFeed, useRotateFeedLink } from '../../hooks/useMonitoring';
import { monitoringErrorOf, rotateMonitoringFeedToken, type IssueFeedInput, type MonitoringFeedRow } from '../../lib/monitoring-api';
import type { Capability } from '../../lib/monitoring-capabilities';
import type { MonitoringSummaryRow } from '../../lib/monitoring-summary';
import {
  absoluteUrl, carriedText, CLASS_ANCHOR, copyText, packMeta, reasonShort, recipientTypeLabel, shortDay, systemTabHref,
} from '../../lib/monitoring-view';
import { fmtDate, plural } from '../../lib/format';
import { CopyField, GatedButton, PackChip, VerifyDisclosure } from './parts';
import { SignPackDialog } from './SignPackDialog';

const DAY = 86_400_000;

/** The public link as people read it: host + "/verify/…" + the last 6 characters (the full url is what Copy copies). */
export function shortLink(path: string): string {
  const abs = absoluteUrl(path).replace(/^https?:\/\//, '');
  const host = abs.split('/')[0] ?? '';
  return `${host}/verify/…${path.slice(-6)}`;
}


function historyLine(h: PackHistoryEntry): string {
  const line = h.transitions.map((t) => `${packMeta(t.status).label} ${shortDay(t.at)}`).join(' → ');
  const span = h.replaced_at ? `${shortDay(h.attested_at)} – ${shortDay(h.replaced_at)}` : `attested ${shortDay(h.attested_at)}`;
  const carried = carriedText(h.carried_at_signing);
  return `Pack v${h.version} · ${span}${line ? `: ${line}` : ''}${carried ? ` · signed carrying ${carried}` : ''}`;
}

/** V113-7-1: "Signed by <name>" plus the carried-exceptions reason the signer typed (internal read only). */
export function SignedBy({ h, lead = false }: { h: PackHistoryEntry; lead?: boolean }) {
  if (!h.signed_by_name && !h.exception_reason) return null;
  const who = h.signed_by_name ? `Signed by ${h.signed_by_name}` : 'Signed';
  return (
    <span className={`mon-sub mon-signed${lead ? ' mon-signed--lead' : ''}`} data-part="signed-by">
      {who}{h.exception_reason ? <>: <q>{h.exception_reason}</q></> : null}
    </span>
  );
}

export function PackValidityPanel({ validity, history, rows, prefix, canSign, onSign }: {
  validity: PackValidity | null; history: PackHistoryEntry[]; rows: MonitoringSummaryRow[]; prefix: string; canSign: Capability; onSign: () => void;
}) {
  const names = new Map(rows.map((r) => [r.system_id, r.name]));
  if (!validity) {
    return (
      <section className="mon-col" aria-labelledby="mon-validity-h">
        <div className="mon-card-head"><h3 id="mon-validity-h">Pack validity</h3></div>
        <p className="mon-pack-line"><PackChip state={null} /></p>
        <p className="mon-hint">Recipients see monitoring once a pack is signed.</p>
        <GatedButton cap={canSign} ask="Ask an owner or admin" variant="primary" onClick={onSign}>Sign the first pack</GatedButton>
      </section>
    );
  }
  const carried = carriedText(validity.carried);
  // V113-7-1: the current pack's signer and reason, when it was signed carrying exceptions.
  const current = history.find((h) => h.version === validity.version && h.carried_at_signing) ?? null;
  return (
    <section className="mon-col" aria-labelledby="mon-validity-h">
      <div className="mon-card-head">
        <h3 id="mon-validity-h">Pack validity</h3>
        <GatedButton cap={canSign} ask="Ask an owner or admin" onClick={onSign}>Sign a new pack</GatedButton>
      </div>
      <p className="mon-pack-line" id="mon-validity-status" tabIndex={-1}>
        <PackChip state={validity.status} />
        <span className="mon-sub">since {fmtDate(validity.status_changed_at)}</span>
        {carried ? <strong>{carried}</strong> : null}
      </p>
      {current ? <SignedBy h={current} lead /> : null}
      <dl className="mon-dl">
        <dt>Pack attested</dt><dd>v{validity.version} · {fmtDate(validity.signed_at)}</dd>
        <dt>Last verified</dt><dd>{fmtDate(validity.computed_at)}</dd>
        <dt>History</dt>
        <dd>
          <ul className="mon-history" aria-label="Pack history, newest first">
            {history.map((h) => (
              <li key={h.package_id}>
                <span>{historyLine(h)}</span>
                <SignedBy h={h} />
              </li>
            ))}
          </ul>
        </dd>
      </dl>
      {validity.reasons.length ? (
        <details className="mon-disclose" id="mon-reasons">
          <summary>{plural(validity.reasons.length, 'reason')}</summary>
          <ul className="mon-reasons">
            {validity.reasons.map((r) => {
              const sys = [...new Set(r.items.map((i) => i.system_id).filter((s): s is string => !!s))];
              const cause = r.items.find((i) => i.cause)?.cause?.label ?? null;
              return (
                <li key={r.code}>
                  <span><strong>{r.label || reasonShort(r.code)}</strong>{r.since ? <span className="mon-sub"> · since {fmtDate(r.since)}</span> : null}</span>
                  <span className="mon-sub">{r.effect === 'invalidated' ? 'Invalidates the pack' : 'Makes the pack stale'} · {r.clears === 'resign' ? 'clears with a new pack' : 'clears when fixed'}</span>
                  {cause ? <span className="mon-sub">{cause}</span> : null}
                  {sys.length ? (
                    <span className="mon-line">{sys.map((s) => {
                      const row = rows.find((x) => x.system_id === s);
                      const name = names.get(s) ?? 'this system';
                      if (r.code === 'new_system_not_in_pack' || r.code === 'new_system_without_authority') {
                        return row?.system_class
                          ? <span key={s}>{name} · <button type="button" className="gp-linkbtn gp-linkbtn--compact" onClick={onSign}>Add to next pack</button></span>
                          : <span key={s}>{name} · <Link to={systemTabHref(prefix, s, CLASS_ANCHOR)}>Set class</Link></span>;
                      }
                      return <Link key={s} to={systemTabHref(prefix, s, 'mon-controls')}>Open {name}</Link>;
                    })}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </details>
      ) : null}
      {validity.next_at_risk_at ? (() => {
        const atRisk = validity.reasons.find((r) => r.code === validity.next_at_risk_code)?.items.find((i) => i.system_id)?.system_id ?? null;
        return (
          <p className="mon-line"><StatusGlyph kind="clock" size={12} /><span>Next at risk: <strong>{fmtDate(validity.next_at_risk_at)}</strong></span>
            {validity.next_at_risk_code ? <span>· {reasonShort(validity.next_at_risk_code)}{atRisk && names.get(atRisk) ? ` · ${names.get(atRisk)}` : ''}</span> : null}
            {atRisk ? <Link to={systemTabHref(prefix, atRisk, 'mon-controls')}>Open</Link> : null}</p>
        );
      })() : null}
      <VerifyDisclosure label="Verify pack">
        <dl className="mon-dl">
          {history.map((h) => (<Fragment key={h.package_id}><dt>v{h.version} digest</dt><dd className="mon-mono">{h.content_sha256}</dd></Fragment>))}
        </dl>
      </VerifyDisclosure>
    </section>
  );
}

/** Done stays disabled until the token was copied or "I saved the token" is ticked; the token is copied from data-value. */
export function TokenDialog({ open, recipient, readUrl, getToken, subtitle = 'Shown once. Send both over a secure channel.', onDone }: {
  open: boolean; recipient: string; readUrl: string | null; getToken: () => string | null; subtitle?: string; onDone: () => void;
}) {
  const uid = useId();
  const ref = useRef<HTMLDialogElement>(null);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [bothFailed, setBothFailed] = useState(false);
  const [nudge, setNudge] = useState(false);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      setCopied(false); setSaved(false); setBothFailed(false); setNudge(false);
      if (typeof d.showModal === 'function') d.showModal(); else d.setAttribute('open', '');
      d.querySelector<HTMLElement>('h2')?.focus();
    }
    if (!open && d.open) { if (typeof d.close === 'function') d.close(); else d.removeAttribute('open'); }
  }, [open]);
  const token = getToken() ?? '';
  const feedUrl = readUrl ? absoluteUrl(readUrl) : '';
  const ready = copied || saved;
  const done = () => { if (!ready) { setNudge(true); return; } onDone(); };
  return (
    <dialog ref={ref} className="gp-dialog" aria-labelledby={`${uid}-t`} onCancel={(e) => { e.preventDefault(); done(); }}>
      {open && readUrl ? (
        <form className="gp-dialog-form" noValidate onSubmit={(e) => { e.preventDefault(); done(); }}>
          <div className="gp-dialog-head">
            <h2 id={`${uid}-t`} tabIndex={-1}>Token for {recipient}</h2>
            <p className="gp-dialog-sub">{subtitle}</p>
          </div>
          <div className="gp-dialog-body gp-form">
            <CopyField label="Token" value={token} onCopied={() => setCopied(true)} />
            <CopyField label="Feed address" value={feedUrl} />
            <div className="mon-actions">
              <Button onClick={async () => { const ok = await copyText(`${token}\n${feedUrl}`); if (ok) setCopied(true); setBothFailed(!ok); }}>Copy both</Button>
            </div>
            {bothFailed ? <p className="mon-err" role="alert">Copy didn’t work here. Select the token and copy it with your keyboard.</p> : null}
            <Checkbox id={`${uid}-saved`} checked={saved} label="I saved the token" onChange={(e) => setSaved(e.currentTarget.checked)} />
          </div>
          <div className="gp-dialog-foot gp-actions gp-actions--end" data-part="dialog-footer">
            {!ready ? <p id={`${uid}-why`} className={nudge ? 'mon-err' : 'mon-hint'} role={nudge ? 'alert' : undefined}>Copy or tick “I saved the token” first</p> : null}
            <Button type="submit" variant="primary" aria-disabled={!ready || undefined} aria-describedby={!ready ? `${uid}-why` : undefined}>Done</Button>
          </div>
        </form>
      ) : null}
    </dialog>
  );
}

function IssueDialog({ open, busy, error, onClose, onIssue }: {
  open: boolean; busy: boolean; error: string | null; onClose: () => void; onIssue: (input: IssueFeedInput) => Promise<boolean>;
}) {
  const uid = useId();
  const defaultExpiry = () => new Date(Date.now() + 365 * DAY).toISOString().slice(0, 10);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [type, setType] = useState<'insurer' | 'customer'>('insurer');
  const [until, setUntil] = useState(defaultExpiry());
  const [errs, setErrs] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!open) return;
    setName(''); setEmail(''); setType('insurer'); setUntil(defaultExpiry()); setErrs({});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <ActionDialog open={open} title="Add recipient" submitLabel="Issue token"
      onClose={onClose} busy={busy} error={error}
      onSubmit={async () => {
        const e: Record<string, string> = {};
        if (!name.trim()) e.name = 'Name the recipient.';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = 'Enter a contact email.';
        if (!until || Date.parse(`${until}T23:59:59Z`) <= Date.now()) e.until = 'Pick a date in the future.';
        setErrs(e);
        const first = e.name ? `${uid}-n` : e.email ? `${uid}-e` : e.until ? `${uid}-u` : null;
        if (first) { setTimeout(() => document.getElementById(first)?.focus(), 0); return false; }
        return onIssue({ recipient_name: name.trim(), recipient_contact_email: email.trim(), recipient_type: type, expires_at: new Date(`${until}T23:59:59Z`).toISOString() });
      }}>
      <Field id={`${uid}-n`} label="Recipient" required error={errs.name}>
        <Input value={name} onChange={(e) => setName(e.currentTarget.value)} placeholder="e.g. Harbor Mutual" />
      </Field>
      <Field id={`${uid}-e`} label="Contact email" required error={errs.email}>
        <Input type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
      </Field>
      <fieldset className="mon-choices">
        <legend>Type</legend>
        {(['insurer', 'customer'] as const).map((t) => (
          <Radio key={t} id={`${uid}-ty-${t}`} name={`${uid}-ty`} checked={type === t} label={recipientTypeLabel(t)} onChange={() => setType(t)} />
        ))}
      </fieldset>
      <Field id={`${uid}-u`} label="Access until" required error={errs.until}>
        <Input type="date" value={until} onChange={(e) => setUntil(e.currentTarget.value)} />
      </Field>
    </ActionDialog>
  );
}

export function SharedFeeds({ slug, prefix, canManage, validity, history, rows, onAnnounce, signOpen, setSignOpen }: {
  slug: string; prefix: string; canManage: Capability; validity: PackValidity | null; history: PackHistoryEntry[];
  rows: MonitoringSummaryRow[]; onAnnounce: (m: string) => void; signOpen: boolean; setSignOpen: (open: boolean) => void;
}) {
  const feeds = useMonitoringFeeds(canManage.allowed);
  const issue = useIssueFeed();
  const revoke = useRevokeFeed();
  const rotate = useRotateFeedLink();
  const replaceAccess = async () => {
    const f = replacing;
    if (!f || replaceBusy) return false;
    setReplaceBusy(true); setReplaceError(null);
    try {
      // New link and token: the link first (its old url answers "replaced"), then the token. Token only: the link stays.
      if (replaceChoice === 'link') await rotate.mutateAsync(f.grant_id);
      const out = await rotateMonitoringFeedToken(slug, f.grant_id);
      rotatedRef.current = { token: out.token, read_url: out.read_url, recipient: f.recipient_name, grant_id: f.grant_id };
      void qc.invalidateQueries({ queryKey: monitoringKeys.feeds(slug) });
      setReplacing(null);
      setRotated({ read_url: out.read_url, recipient: f.recipient_name, grant_id: f.grant_id, newLink: replaceChoice === 'link' });
      return true;
    } catch (e) {
      const x = monitoringErrorOf(e);
      setReplaceError(x.kind === 'forbidden' ? 'Only an owner or admin can replace access.' : x.kind === 'not_found' ? 'This recipient’s access has ended. Refresh the list.' : x.message || 'Access could not be replaced.');
      return false;
    } finally { setReplaceBusy(false); }
  };
  const [issueOpen, setIssueOpen] = useState(false);
  const qc = useQueryClient();
  // Replace token (rotate-token): the full new token lives in this ref only, never in the cache or the DOM text.
  const rotatedRef = useRef<{ token: string; read_url: string; recipient: string; grant_id: string } | null>(null);
  const [rotated, setRotated] = useState<{ read_url: string; recipient: string; grant_id: string; newLink: boolean } | null>(null);
  const [replacing, setReplacing] = useState<MonitoringFeedRow | null>(null);
  const [replaceChoice, setReplaceChoice] = useState<'token' | 'link'>('token');
  const [replaceBusy, setReplaceBusy] = useState(false);
  const [replaceError, setReplaceError] = useState<string | null>(null);
  const focusAfter = useFocusAfter();
  useEffect(() => () => { rotatedRef.current = null; }, []);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [tokenFor, setTokenFor] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<MonitoringFeedRow | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const list = feeds.data?.feeds.filter((f) => f.status === 'active') ?? [];

  return (
    <div className="mon-feeds">
      <PackValidityPanel validity={validity} history={history} rows={rows} prefix={prefix} canSign={canManage} onSign={() => setSignOpen(true)} />
      <section className="mon-col" aria-labelledby="mon-recipients-h">
        <div className="mon-card-head">
          <h3 id="mon-recipients-h" tabIndex={-1}>Recipients</h3>
          <GatedButton cap={canManage} ask="Ask an owner or admin" onClick={() => { setIssueError(null); setIssueOpen(true); }}>Add recipient</GatedButton>
        </div>
        {!canManage.allowed ? <p className="mon-hint">Owners and admins share the signed monitoring status with insurers and customers.</p>
          : feeds.loading ? <Spinner label="Loading recipients" />
            : feeds.error ? <Alert variant="error">Recipients could not be loaded. <Button compact onClick={() => void feeds.refetch()}>Retry</Button></Alert>
              : !list.length ? <p className="mon-hint">{validity ? 'No recipient yet. Add an insurer or customer to share the signed status.' : 'Sign a pack, then add a recipient.'}</p>
                : (
                  <ul className="mon-rows" aria-label="Recipients">
                    {list.map((f) => (
                      <li key={f.grant_id} className="mon-recipient" id={`mon-feed-${f.grant_id}`} tabIndex={-1}>
                        <span className="mon-row-main">
                          <span><span className="mon-recipient-name">{f.recipient_name}</span> <span className="mon-sub">· {recipientTypeLabel(f.recipient_type)}</span></span>
                          <span className="mon-sub">Expires {fmtDate(f.expires_at)} · {f.last_access_at ? `last read ${fmtDate(f.last_access_at)}` : 'not read yet'}</span>
                        </span>
                        <span className="mon-row-acts">
                          <Button compact onClick={() => { setReplacing(f); setReplaceChoice('token'); setReplaceError(null); }}>Replace access…</Button>
                          <Button compact onClick={() => { setRevoking(f); setRevokeError(null); }}>Revoke</Button>
                        </span>
                        {f.verify_url ? (
                          <details className="mon-disclose mon-recipient-link">
                            <summary>Link<span className="gp-sr-only"> for {f.recipient_name}</span></summary>
                            <div className="mon-col">
                              <CopyField label={`Public link for ${f.recipient_name}`} hideLabel value={absoluteUrl(f.verify_url)} display={shortLink(f.verify_url)} />
                              <span className="mon-sub">Issued {fmtDate(f.created_at)} · <a href={f.verify_url} target="_blank" rel="noreferrer">Open as the recipient sees it<span className="gp-sr-only"> (opens in a new tab)</span></a></span>
                            </div>
                          </details>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}

        {list.length ? (
          <VerifyDisclosure label="Verify links">
            <dl className="mon-dl">
              {list.map((f) => (<Fragment key={f.grant_id}><dt>{f.recipient_name}</dt><dd className="mon-mono">token …{(f.token_hints[0] ?? '').replace(/^…/, '')} · link {f.link_id ?? '—'}</dd></Fragment>))}
            </dl>
          </VerifyDisclosure>
        ) : null}
      </section>

      <SignPackDialog open={signOpen} slug={slug} prefix={prefix} validity={validity} previousSignedAt={validity?.signed_at ?? null} rows={rows}
        onClose={() => setSignOpen(false)} onSigned={(m) => { setSignOpen(false); onAnnounce(m); }} />

      <IssueDialog open={issueOpen} busy={issue.isPending} error={issueError} onClose={() => setIssueOpen(false)}
        onIssue={async (input) => {
          setIssueError(null);
          try {
            await issue.issue(input);
            setIssueOpen(false);
            setTokenFor(input.recipient_name);
            return true;
          } catch (e) {
            const f = monitoringErrorOf(e);
            setIssueError(f.kind === 'no_pack' ? 'Sign a pack before sharing a monitoring feed.' : f.kind === 'not_configured' ? 'Signing isn’t set up for this workspace yet.' : f.message || 'The token could not be issued.');
            return false;
          }
        }} />

      <TokenDialog open={!!tokenFor && !!issue.issued} recipient={tokenFor ?? ''} readUrl={issue.issued?.read_url ?? null} getToken={issue.getToken}
        onDone={() => {
          const who = tokenFor;
          const grant = issue.issued?.feed.grant_id;
          issue.forget(); setTokenFor(null);
          onAnnounce(`Token issued for ${who}`);
          if (grant) focusAfter(`mon-feed-${grant}`);
        }} />

      <TokenDialog open={!!rotated} recipient={rotated?.recipient ?? ''} readUrl={rotated?.read_url ?? null} getToken={() => rotatedRef.current?.token ?? null}
        subtitle={rotated?.newLink ? 'Shown once. Their old link and token stop working; send them the new link (on the recipient card) and this token.' : 'Shown once. The link stays the same; the old token stops working.'}
        onDone={() => {
          const r = rotated;
          rotatedRef.current = null; setRotated(null);
          if (r) { onAnnounce(r.newLink ? `Send ${r.recipient} the new link and token` : `New token for ${r.recipient} · the old one no longer works`); focusAfter(`mon-feed-${r.grant_id}`); }
        }} />

      <ActionDialog open={!!replacing} title={`Replace access · ${replacing?.recipient_name ?? ''}`} busy={replaceBusy} error={replaceError}
        submitLabel={replaceChoice === 'link' ? 'Replace link and token' : 'Replace token'} danger={replaceChoice === 'link'}
        onClose={() => setReplacing(null)} onSubmit={replaceAccess}>
        <fieldset className="mon-radio-list">
          <legend>What to replace</legend>
          <Radio id="mon-replace-token" name="mon-replace" checked={replaceChoice === 'token'} onChange={() => setReplaceChoice('token')}
            label="A new token; the link stays the same" description={`${replacing?.recipient_name ?? 'The recipient'}'s current token stops working. Their bookmarked link keeps working.`} />
          <Radio id="mon-replace-link" name="mon-replace" checked={replaceChoice === 'link'} onChange={() => setReplaceChoice('link')}
            label="A new link and a new token" description={`${replacing?.recipient_name ?? 'The recipient'}'s bookmarked link stops working (it shows “replaced”), and so does their token. Send them both.`} />
        </fieldset>
      </ActionDialog>

      <ActionDialog open={!!revoking} title={`Revoke ${revoking?.recipient_name ?? ''}`} submitLabel="Revoke" danger busy={revoke.isPending} error={revokeError}
        onClose={() => setRevoking(null)}
        onSubmit={async () => {
          if (!revoking) return false;
          try { await revoke.mutateAsync(revoking.grant_id); onAnnounce(`${revoking.recipient_name} can no longer read the feed`); setRevoking(null); focusAfter('mon-recipients-h'); return true; }
          catch (e) { setRevokeError(monitoringErrorOf(e).message || 'Access could not be revoked.'); return false; }
        }}>
        <p className="mon-hint">Their token and public link stop working now.</p>
      </ActionDialog>
    </div>
  );
}
